import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Heart, Compass, Dress, Handbag } from "@phosphor-icons/react";
import SEO from "@/components/SEO";

const experiences = [
  {
    slug: "mariage",
    title: "Mariage",
    subtitle: "Votre jour, notre excellence",
    desc: "Un service de chauffeur privé sur mesure pour le plus beau jour de votre vie. Véhicules décorés, mise à disposition toute la journée.",
    image: "https://customer-assets.emergentagent.com/job_parisgo/artifacts/iuadc8o6_4D3F491D-0EEA-41C6-A7A7-DBFF5456CB20.png",
    icon: Heart,
  },
  {
    slug: "paris-sur-mesure",
    title: "Paris sur mesure",
    subtitle: "Votre Paris, votre rythme",
    desc: "Découvrez Paris autrement avec un chauffeur privé dédié. Monuments, quartiers secrets, gastronomie — un itinéraire conçu pour vous.",
    image: "https://images.pexels.com/photos/34773158/pexels-photo-34773158.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    icon: Compass,
  },
  {
    slug: "fashion-week",
    title: "Fashion Week",
    subtitle: "Transport privé haute couture",
    desc: "Naviguez entre les défilés, showrooms et soirées de la Paris Fashion Week avec élégance et ponctualité.",
    image: "https://images.unsplash.com/photo-1732210571843-6789f9090b0e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
    icon: Dress,
  },
  {
    slug: "shopping-luxe",
    title: "Shopping luxe",
    subtitle: "L'art du shopping avec chauffeur",
    desc: "Avenue Montaigne, Place Vendôme, Faubourg Saint-Honoré — votre chauffeur vous accompagne dans les plus belles adresses parisiennes.",
    image: "https://images.pexels.com/photos/19789287/pexels-photo-19789287.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    icon: Handbag,
  },
];

const ExperiencesPage = () => {
  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid="experiences-page">
      <SEO
        title="Expériences avec Chauffeur Privé Paris"
        path="/experiences"
        description="Vivez des expériences uniques avec chauffeur privé ALTURA à Paris : mariage, Paris sur mesure, Fashion Week, shopping luxe. Service haut de gamme personnalisé."
      />

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-4">ALTURA · Sur mesure</p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1A1A1A] mb-4">
              Nos expériences
            </h1>
            <p className="text-[#1A1A1A]/70 text-base max-w-2xl mx-auto font-light">
              Des moments d'exception pensés pour vous. Chaque expérience est unique, portée par un service de chauffeur privé irréprochable.
            </p>
          </motion.div>

          <div className="space-y-8">
            {experiences.map((exp, index) => (
              <motion.div
                key={exp.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`grid lg:grid-cols-2 gap-0 ${index % 2 === 1 ? "lg:grid-flow-dense" : ""}`}
              >
                <div className={`image-zoom h-[300px] lg:h-[450px] ${index % 2 === 1 ? "lg:col-start-2" : ""}`}>
                  <img src={exp.image} alt={exp.title} className="w-full h-full object-cover" />
                </div>

                <div className={`bg-white border border-[#1A1A1A]/10 p-8 lg:p-14 flex flex-col justify-center ${index % 2 === 1 ? "lg:col-start-1" : ""}`}>
                  <exp.icon size={36} weight="thin" className="text-[#1A1A1A] mb-5" />
                  <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-2">{exp.subtitle}</p>
                  <h2 className="font-heading text-2xl lg:text-3xl text-[#1A1A1A] mb-4">{exp.title}</h2>
                  <p className="text-[#1A1A1A]/80 font-light leading-relaxed mb-8">{exp.desc}</p>

                  <Link
                    to={`/experiences/${exp.slug}`}
                    className="btn-primary inline-flex items-center gap-3 w-fit"
                    data-testid={`experience-cta-${exp.slug}`}
                  >
                    Découvrir
                    <ArrowRight size={16} weight="bold" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24 sm:py-32 bg-[#EDEBE4] relative">
        <div className="texture-overlay" />
        <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-3xl sm:text-4xl font-normal text-[#1A1A1A] mb-8">
              Une demande spécifique ?
            </h2>
            <p className="text-[#1A1A1A]/80 font-light mb-12 max-w-2xl mx-auto">
              Chaque expérience est personnalisable. Contactez-nous pour créer un moment sur mesure.
            </p>
            <Link to="/contact" className="btn-dark inline-flex items-center gap-3" data-testid="experiences-contact-cta">
              Nous contacter
              <ArrowRight size={16} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default ExperiencesPage;
